import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { getAllChats, getChatMessages, sendMessage } from "../../services/chat";
import logoImage from "../../assets/images/logo.png";
import "../../styles/Messages.css";

// ── Icons ─────────────────────────────────────────────
const SendIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const UserIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const ChatBubbleIcon = () => (
  <svg
    width="44"
    height="44"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#d5c9bf"
    strokeWidth="1.4"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const Spinner = ({ size = 14, color = "#fff" }) => (
  <span
    style={{
      display: "inline-block",
      width: size,
      height: size,
      border: `2px solid ${color}33`,
      borderTopColor: color,
      borderRadius: "50%",
      animation: "msg-spin 0.65s linear infinite",
      flexShrink: 0,
    }}
  />
);

// ── Helpers ───────────────────────────────────────────
const getSafeImage = (src) => {
  return src && String(src).trim() ? src : logoImage;
};

const parseDate = (ts) => {
  if (!ts) return null;

  const date = new Date(ts);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatMessageTime = (ts) => {
  const date = parseDate(ts);
  if (!date) return "";

  return date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const formatChatListTime = (ts) => {
  const date = parseDate(ts);
  if (!date) return "";

  const now = new Date();

  const sameDay =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();

  if (sameDay) {
    return date.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }

  const yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);

  const isYesterday =
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();

  if (isYesterday) return "Yesterday";

  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
  });
};

const normalizeChat = (chat) => {
  const id = chat.id || chat.chat_id || chat.chatId;

  return {
    ...chat,
    id,
    vendorDisplayName:
      chat.vendor?.unique_name ||
      chat.vendor?.name ||
      chat.vendor_name ||
      chat.vendorName ||
      chat.store_name ||
      "Vendor",
    vendorImage: getSafeImage(
      chat.vendor?.profile_image ||
        chat.vendor_logo ||
        chat.vendorLogo ||
        chat.store_logo
    ),
    vendorOnline: Boolean(chat.vendor?.is_online),
    productId: chat.product?.id || chat.product_id || chat.productId || null,
    productName: chat.product?.name || chat.product_name || "",
    latestText:
      chat.latest_message?.message ||
      chat.last_message?.message ||
      chat.last_message ||
      chat.lastMessage ||
      "No messages yet",
    latestTime:
      chat.latest_message?.created_at ||
      chat.updated_at ||
      chat.updatedAt ||
      chat.created_at ||
      null,
  };
};

const normalizeMessagesResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.messages)) return data.messages;
  if (Array.isArray(data?.data?.messages)) return data.data.messages;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

// ── Component ─────────────────────────────────────────
export default function Messages() {
  const [selectedChat, setSelectedChat] = useState(null);
  const [chats, setChats] = useState([]);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [sending, setSending] = useState(false);

  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchChats();
  }, []);

  useEffect(() => {
    if (selectedChat?.id) {
      fetchMessages(selectedChat.id);
    }
  }, [selectedChat?.id]);

  useEffect(() => {
    if (!selectedChat?.id) return;

    const interval = setInterval(() => {
      fetchMessages(selectedChat.id, false);
      fetchChats(false);
    }, 5000);

    return () => clearInterval(interval);
  }, [selectedChat?.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchChats = async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);

      const data = await getAllChats();
      const rawChats = Array.isArray(data)
        ? data
        : data?.chats || data?.data || [];

      const normalizedChats = rawChats.map(normalizeChat);
      setChats(normalizedChats);

      if (normalizedChats.length > 0) {
        setSelectedChat((prev) => {
          if (!prev?.id) return normalizedChats[0];
          const matched = normalizedChats.find((c) => c.id === prev.id);
          return matched || normalizedChats[0];
        });
      } else {
        setSelectedChat(null);
      }
    } catch (error) {
      console.error("Failed to load chats:", error);
      if (showLoader) toast.error("Failed to load chats.");
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  const fetchMessages = async (chatId, showLoader = true) => {
    if (!chatId) return;

    try {
      if (showLoader) setLoadingMsgs(true);

      const data = await getChatMessages(chatId);
      const list = normalizeMessagesResponse(data);

      setMessages(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Failed to load messages:", error);
      if (showLoader) toast.error("Failed to load messages.");
    } finally {
      if (showLoader) setLoadingMsgs(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedChat) return;

    const productId =
      selectedChat.productId ||
      selectedChat.product?.id ||
      selectedChat.product_id;

    if (!productId) {
      toast.error("Unable to send: product info missing.");
      return;
    }

    const trimmedMessage = messageInput.trim();

    const tempMessage = {
      id: `temp-${Date.now()}`,
      sender_type: "Customer",
      message: trimmedMessage,
      created_at: new Date().toISOString(),
      is_temp: true,
    };

    try {
      setSending(true);

      setMessages((prev) => [...prev, tempMessage]);
      setMessageInput("");

      await sendMessage({
        chatId: selectedChat.id,
        productId,
        message: trimmedMessage,
      });

      await fetchMessages(selectedChat.id, false);
      await fetchChats(false);
    } catch (error) {
      console.error("Failed to send message:", error);
      toast.error("Failed to send message.");

      setMessages((prev) => prev.filter((msg) => msg.id !== tempMessage.id));
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="msg-root">
      <div className="acc-head" style={{ marginBottom: 16 }}>
        <div>
          <h1 className="acc-h1">My Messages</h1>
          {!loading && chats.length > 0 && (
            <p className="msg-chat-count">
              {chats.length} conversation{chats.length !== 1 ? "s" : ""}
            </p>
          )}
        </div>
      </div>

      {loading ? (
        <div className="acc-box msg-loading-box">
          <Spinner size={28} color="#E67E42" />
          <span>Loading your messages…</span>
        </div>
      ) : chats.length === 0 ? (
        <div className="acc-box msg-empty-box">
          <ChatBubbleIcon />
          <div className="msg-empty-title">No conversations yet</div>
          <div className="msg-empty-sub">
            Start a chat with a vendor from any product page.
          </div>
        </div>
      ) : (
        <div className="msg-layout">
          <div className="msg-list-panel">
            <div className="msg-list-label">Conversations</div>

            <div className="msg-list">
              {chats.map((chat) => {
                const isActive = selectedChat?.id === chat.id;

                return (
                  <div
                    key={chat.id}
                    className={`msg-chat-item ${isActive ? "active" : ""}`}
                    onClick={() => setSelectedChat(chat)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelectedChat(chat);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="msg-chat-avatar">
                      <img
                        src={chat.vendorImage}
                        alt={chat.vendorDisplayName}
                        onError={(e) => {
                          e.target.src = logoImage;
                        }}
                      />
                      {chat.vendorOnline && <span className="msg-unread-dot" />}
                    </div>

                    <div className="msg-chat-info">
                      <div className="msg-chat-name">
                        {chat.vendorDisplayName}
                      </div>
                      <div className="msg-chat-preview">{chat.latestText}</div>
                    </div>

                    <div className="msg-chat-time">
                      {formatChatListTime(chat.latestTime)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {selectedChat ? (
            <div className="msg-chat-panel">
              <div className="msg-chat-header">
                <div className="msg-chat-header-avatar">
                  <img
                    src={selectedChat.vendorImage}
                    alt={selectedChat.vendorDisplayName}
                    onError={(e) => {
                      e.target.src = logoImage;
                    }}
                  />
                </div>

                <div className="msg-chat-header-info">
                  <div className="msg-chat-header-name">
                    {selectedChat.vendorDisplayName}
                  </div>

                  {selectedChat.productName && (
                    <div className="msg-chat-header-product">
                      Re: {selectedChat.productName}
                    </div>
                  )}
                </div>
              </div>

              <div className="msg-messages-area">
                {loadingMsgs ? (
                  <div className="msg-messages-loading">
                    <Spinner size={22} color="#E67E42" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="msg-messages-empty">
                    <ChatBubbleIcon />
                    <p>No messages yet. Say hello! 👋</p>
                  </div>
                ) : (
                  messages.map((msg, idx) => {
                    const senderType = String(
                      msg.sender_type || msg.senderType || ""
                    ).toLowerCase();

                    const isVendor = senderType === "vendor";
                    const text = msg.message || msg.text || msg.content || "";
                    const ts =
                      msg.created_at || msg.createdAt || msg.timestamp || "";
                    const msgId =
                      msg.id || msg.message_id || msg.messageId || `msg-${idx}`;

                    const messageProduct = msg.product || msg.productInfo || null;
                    const messageProductImage = getSafeImage(
                      messageProduct?.image ||
                        messageProduct?.thumbnail ||
                        messageProduct?.product_images?.[0]
                    );

                    return (
                      <div
                        key={msgId}
                        className={`msg-bubble-row ${
                          isVendor ? "vendor" : "user"
                        } ${msg.is_temp ? "sending" : ""}`}
                      >
                        <div className="msg-bubble-avatar">
                          {isVendor ? (
                            <img
                              src={selectedChat.vendorImage}
                              alt={selectedChat.vendorDisplayName}
                              onError={(e) => {
                                e.target.src = logoImage;
                              }}
                            />
                          ) : (
                            <div className="msg-user-avatar">
                              <UserIcon />
                            </div>
                          )}
                        </div>

                        <div className="msg-bubble">
                          {messageProduct && (
                            <div className="msg-product-card">
                              <img
                                src={messageProductImage}
                                alt={messageProduct?.name || "Product"}
                                onError={(e) => {
                                  e.target.src = logoImage;
                                }}
                              />
                              <div className="msg-product-info">
                                <div className="msg-product-name">
                                  {messageProduct?.name}
                                </div>
                                {(messageProduct?.price ||
                                  messageProduct?.final_price) && (
                                  <div className="msg-product-price">
                                    $
                                    {messageProduct?.price ||
                                      messageProduct?.final_price}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {text && <p className="msg-bubble-text">{text}</p>}

                          <span className="msg-bubble-time">
                            {msg.is_temp ? "Sending..." : formatMessageTime(ts)}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}

                <div ref={messagesEndRef} />
              </div>

              <div className="msg-input-bar">
                <input
                  type="text"
                  className="msg-input"
                  placeholder="Type a message..."
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={sending}
                />

                <button
                  className="msg-send-btn"
                  onClick={handleSendMessage}
                  disabled={sending || !messageInput.trim()}
                  title="Send"
                >
                  {sending ? <Spinner size={14} /> : <SendIcon />}
                </button>
              </div>
            </div>
          ) : (
            <div className="msg-chat-panel msg-chat-placeholder">
              <ChatBubbleIcon />
              <p>Select a conversation to start chatting</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}